import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Tag } from 'antd';
import { FiCheck, FiRefreshCw, FiX } from 'react-icons/fi';
import { AiOutlineDelete } from 'react-icons/ai';
import { DataTable } from '@components/data-table';
import { TooltipTop } from '@components/tooltips';
import { AirflowTaskInstance } from '../../types';
import { formatDateTime, formatDurationSeconds, TaskInstanceStateBadge } from '../../utils';
import styles from '../../styles.module.scss';

type InstancesTableProps = {
    tasks: AirflowTaskInstance[];
    isLoading: boolean;
    onTaskClick: (task: AirflowTaskInstance) => void;
    onClear: (task: AirflowTaskInstance) => void;
    onMarkSuccess: (task: AirflowTaskInstance) => void;
    onMarkFailed: (task: AirflowTaskInstance) => void;
    onDeleteRequest: (task: AirflowTaskInstance) => void;
};

/*
 * Task-instance table shared by the run-scoped Task Instances tab and reused elsewhere
 */
export function InstancesTable({ tasks, isLoading, onTaskClick, onClear, onMarkSuccess, onMarkFailed, onDeleteRequest }: InstancesTableProps) {
    const columns = useMemo<ColumnDef<AirflowTaskInstance>[]>(
        () => [
            { id: 'task_id', header: 'Task ID', enableSorting: false, cell: ({ row }) => row.original.task_id },
            { id: 'map_index', header: 'Map Index', enableSorting: false, cell: ({ row }) => ((row.original.map_index ?? -1) >= 0 ? row.original.map_index : '—') },
            { id: 'state', header: 'State', enableSorting: false, cell: ({ row }) => <TaskInstanceStateBadge state={row.original.state} /> },
            { id: 'start_date', header: 'Start Date', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.start_date) },
            { id: 'try_number', header: 'Try Number', enableSorting: false, cell: ({ row }) => row.original.try_number ?? 1 },
            { id: 'operator', header: 'Operator', enableSorting: false, cell: ({ row }) => <Tag>{row.original.operator_name ?? row.original.operator ?? '—'}</Tag> },
            { id: 'duration', header: 'Duration', enableSorting: false, cell: ({ row }) => formatDurationSeconds(row.original.duration) },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: ({ row }) => {
                    const task = row.original;
                    return (
                        <span style={{ display: 'flex', gap: 12 }}>
                            <TooltipTop title="Clear">
                                <span
                                    className={styles.actionIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClear(task);
                                    }}
                                >
                                    <FiRefreshCw />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Mark Success">
                                <span
                                    className={styles.actionIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkSuccess(task);
                                    }}
                                >
                                    <FiCheck />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Mark Failed">
                                <span
                                    className={styles.actionIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkFailed(task);
                                    }}
                                >
                                    <FiX />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Delete">
                                <span
                                    className={`${styles.actionIcon} ${styles.destructive}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteRequest(task);
                                    }}
                                >
                                    <AiOutlineDelete />
                                </span>
                            </TooltipTop>
                        </span>
                    );
                },
            },
        ],
        [onClear, onMarkSuccess, onMarkFailed, onDeleteRequest]
    );

    return (
        <DataTable
            data={tasks}
            columns={columns}
            isLoading={isLoading}
            onRowClick={onTaskClick}
            rowKey={(task) => `${task.task_id}-${task.map_index ?? -1}`}
            emptyText="No task instances found"
            maxHeight={400}
        />
    );
}
